import React, { useEffect, useState, useImperativeHandle } from 'react';
import { CaretDownOutlined } from '@ant-design/icons'; 
import { Alert, Button, Checkbox, Layout, Menu, Table, Tag } from 'antd';
import Spinner from '../../Spinner/Spinner';
import SearchBar from '../../SearchBar/SearchBar';
import useSearch from '../../../gql/hooks/useSearch'; 
import useHasError from '../../../hooks/useHasError'; 
import useIsLoading from '../../../hooks/useIsLoading'; 
import './styles.less';

const SearchQueryMiniList = ( { type, tableColumns, selectItemsChangeCallback, 
    initiallyDisabledItems = [], disabled = false, alwaysDisabledItems = [], showType = true, ref } ) => {
    const [ tableData, setTableData ] = useState( [] );
    const [ currentPage, setCurrentPage ] = useState( 1 );
    const [ searchText, setSearchText ] = useState( '' ); 
    const [ sortField, setSortField ] = useState( 'name' ); 
    const [ sortDirection, setSortDirection ] = useState( 'ascend' ); 
    const { searchData, searchDataCount, searchError, searchLoading, loadSearch } = useSearch( { type } );
    const [ selectedItems, setSelectedItems ] = useState( [] );
    const [ disabledItems, setDisabledItems ] = useState( [ ...initiallyDisabledItems, ...alwaysDisabledItems ] );

    const { isLoading } = useIsLoading( { items: [ searchLoading ] } );
    const { hasError } = useHasError( { items: [ searchError ] } );

    useEffect( () => setTableData( searchData ), [ searchData ] );
    useEffect( () => loadSearch( { searchText, currentPage, sortField, sortDirection } ), [] );
    
    useEffect( () => {    
        if ( JSON.stringify( disabledItems ) !== JSON.stringify( initiallyDisabledItems ) ) {
            setDisabledItems( [ ...initiallyDisabledItems, ...alwaysDisabledItems ] );
        }
    }, [ initiallyDisabledItems ] );

    useImperativeHandle( ref, () => ( {
        setSelectedItems: ( items ) => {
            setSelectedItems( items );
            if ( selectItemsChangeCallback ) {
                selectItemsChangeCallback( items );
            }
        },
        addDisabledItems: ( items ) => {
            setDisabledItems( disabledItems.concat( items ) );
        },
        removeDisabledItems: ( items ) => {
            setDisabledItems( disabledItems.filter( item => !items.includes( item ) ) );    
        } 
    } ) );

    const getListItemType = ( item ) => {
        const formatMap = {
            objects: () => item.format,
            courses: () => item.learningObjects?.[ 0 ]?.format,
            categories: () => item.brandName
        };

        const format = formatMap[ type ]?.();
        return format ? <Tag>{ format.toUpperCase() }</Tag> : null;
    };

    const handleSelectCheck = ( event, item ) => {
        const checked = event.target.checked;
        const itemId = item.id;
    
        if ( checked === selectedItems.includes( itemId ) ) {
            return;
        }

        const newSelectedItems = checked 
            ? [ ...selectedItems, itemId ]
            : selectedItems.filter( id => id !== itemId );
    
        setSelectedItems( newSelectedItems );
        selectItemsChangeCallback?.( newSelectedItems );
    };

    const handleToggleSort = ( field ) => {
        let newSortField = '';

        if ( field === 'name' ) {
            newSortField = sortField === 'name' ? '' : 'name';
            setSortField( newSortField );
        }

        if ( field === 'updatedAt' ) {
            newSortField = sortField === 'updatedAt' ? '' : 'updatedAt';
            setSortField( newSortField );
        }

        if ( field === 'createdAt' ) {
            newSortField = sortField === 'createdAt' ? '' : 'createdAt';
            setSortField( newSortField );
        }
        
        loadSearch( { 
            searchText, 
            currentPage: currentPage, 
            sortField: newSortField, 
            sortDirection: sortDirection
        } );
    };

    const handleToggleSortDirection = ( field ) => {
        let newSortDirection = '';

        if ( field === 'ascend' ) {
            newSortDirection = sortDirection === 'ascend' ? '' : 'ascend';
            setSortDirection( newSortDirection );
        }

        if ( field === 'descend' ) {
            newSortDirection = sortDirection === 'descend' ? '' : 'descend';
            setSortDirection( newSortDirection );
        }
        
        loadSearch( { 
            searchText, 
            currentPage: currentPage, 
            sortField: sortField, 
            sortDirection: newSortDirection
        } );
    };

    const renderFooter = () => {
        return disabled ? <span>&nbsp;</span> : <span>{ `${ selectedItems.length } Selected` }</span>;
    };

    const renderSortMenu = ( confirm ) => {
        return (
            <Menu>
                <Menu.Item key="name">
                    <Checkbox
                        checked={ sortField === 'name' }
                        onChange={ ( e ) => {
                            handleToggleSort( 'name' ); 
                            confirm( {
                                closeDropdown: true,
                            } );
                        } }
                    >
                        Name
                    </Checkbox>
                </Menu.Item>

                <Menu.Item key="updatedAt">
                    <Checkbox
                        checked={ sortField === 'updatedAt' }
                        onChange={ ( e ) => {
                            handleToggleSort( 'updatedAt' );  
                            confirm( {
                                closeDropdown: true,
                            } );
                        } }
                    >
                        Updated At
                    </Checkbox>
                </Menu.Item>   
    
                <Menu.Item key="createdAt">
                    <Checkbox
                        checked={ sortField === 'createdAt' }
                        onChange={ ( e ) => {
                            handleToggleSort( 'createdAt' );  
                            confirm( {
                                closeDropdown: true,
                            } );
                        } }
                    >
                        Created At
                    </Checkbox>
                </Menu.Item>   

                <Menu.Divider />

                <Menu.Item key="ascend">
                    <Checkbox
                        checked={ sortDirection === 'ascend' }
                        onChange={ ( e ) => {
                            handleToggleSortDirection( 'ascend' );  
                            confirm( {
                                closeDropdown: true,
                            } );
                        } }
                    >
                        Ascending
                    </Checkbox>
                </Menu.Item>   

                <Menu.Item key="descend">
                    <Checkbox
                        checked={ sortDirection === 'descend' }
                        onChange={ ( e ) => {
                            handleToggleSortDirection( 'descend' );  
                            confirm( {
                                closeDropdown: true,
                            } );
                        } }
                    >
                        Decending
                    </Checkbox>
                </Menu.Item>   
            </Menu>
        );
    };

    // Add the filter and render for name column
    tableColumns.forEach( item => {
        if ( item.sortKey === 'name' ) {
            item.filterDropdown = disabled ? undefined : ( { confirm } ) => renderSortMenu( confirm );
            item.filterIcon = disabled ? undefined : <span>Sort <CaretDownOutlined /></span>;
            item.render = ( name, i ) => {
                return (
                    <div className="flex-container-name-column">
                        
                        { /* Enabled items */ }
                        { !disabled && !disabledItems.includes( i.id ) &&
                            <>
                                <div className="left-name-column">
                                    <Checkbox 
                                        checked={ selectedItems.includes( i.id ) }
                                        onChange={ ( e ) => handleSelectCheck( e, i ) } 
                                    />
                                    { name }
                                </div>

                                { showType &&
                                    <div className="right-name-column">
                                        { getListItemType( i ) }
                                    </div>
                                }
                            </>
                        }

                        { /* Disabled items */ }
                        { ( disabledItems.includes( i.id ) || disabled ) &&
                            <>
                                <div className="left-name-column disabled">
                                    { name }
                                </div>

                                { showType &&
                                    <div className="right-name-column disabled">
                                        { getListItemType( i ) }
                                    </div>
                                }
                            </>                               
                        }
                    </div>
                );
            };
        }
    } );

    /*************************************
     * ANCHOR Event handlers
     *************************************/
    
    const handleChangeCallback = ( pagination ) => {
        setCurrentPage( pagination.current );

        loadSearch( { 
            searchText, 
            currentPage: pagination.current, 
            sortField: sortField, 
            sortDirection: sortDirection
        } );  
    };

    const handleSearchClick = ( value ) => {
        setCurrentPage( 1 ); 
        setSearchText( value );
        loadSearch( { searchText: value, currentPage: 1, sortField, sortDirection } );  
    };

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return (
        <Layout className={ `search-query-mini-list-page ${ disabled ? 'disabled' : '' }` }>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
            
            { /* Main Content */ }
            { !isLoading &&
                <div className="search-query-content">
                    
                    { /* Error message */ }
                    { hasError && 
                        <Alert 
                            style={ { margin: '10px' } }
                            type="error"
                            message="Error"
                            description={
                                <>
                                    <p>An unknown error occurred. Please try again later.</p>
                                    <Button
                                        onClick={ () => loadSearch( { searchText, currentPage, sortField, sortDirection } ) }
                                    >
                                        Reload
                                    </Button>
                                </>
                            }
                        />
                    }
                    
                    { !hasError && 
                        <> 
                            { /* Search bar */ }
                            <SearchBar
                                size="middle"
                                showTags={ false }
                                searchText={ searchText }
                                disabled={ disabled }
                                handleSearchCallback={ handleSearchClick }
                            /> 

                            { /* Main table */ }
                            <Table 
                                loading= { searchLoading }
                                columns={ tableColumns }
                                dataSource={ tableData }
                                onChange={ handleChangeCallback }
                                size="small"
                                enabled={ !disabled }
                                pagination={ disabled ? false : {
                                    current: currentPage,
                                    total: searchDataCount,
                                    position: [ 'bottomRight' ],
                                    showQuickJumper: false,
                                    showSizeChanger: false
                                } }
                                footer={ renderFooter }
                                locale={ {
                                    emptyText: <div style={ { minHeight: '308px' } }></div>,
                                } }
                            />
                        </>
                    }
                </div>
            }
        </Layout>
    );
};

export default SearchQueryMiniList;
